import React, { useState, useEffect } from "react";
import SwoodleUpdatedLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../common/SignUp/TextInput";
import emailIcon from "../../../Assets/Icons/emailIcon.svg";
import passwordIcon from "../../../Assets/Icons/passwordIcon.svg";
import { setLoginData, clearAuth } from "../../../redux/slices/authSlice";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../services/authService";
import { loginSchema } from "../../../utils/validationSchemas";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { getOnboardingStatus } from "../../../services/fundraiserService";
// import { getUserSubscription } from "../../../services/subscriptionService";
import Checkbox from "../../common/Checkbox";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import Loader from "../../common/Loader";
import { getFcmToken } from "../../../utils/connectStore";
import axios from "../../../utils/axios";
import "../../common/OfferForm/offerform.scss"; // Import styles for the modal

const AccountLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberCheckbox, setRememberCheckbox] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showAccountPendingModal, setShowAccountPendingModal] = useState(false);
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const user = useSelector((state) => state?.authReducer?.tokenData?.user);

  // Check for pending approval message from navigation state or success message from Stripe completion
  useEffect(() => {
    let shouldShowModal = false;
    let modalHandled = false;
    
    // Check for success message from Stripe completion
    if (sessionStorage.getItem('fundraiser_stripe_complete') === 'true') {
      setSuccessMessage("Your Stripe account setup is complete! Please log in to access your dashboard.");
      sessionStorage.removeItem('fundraiser_stripe_complete');
    }
    
    // Check for account pending modal flag in sessionStorage
    if (sessionStorage.getItem('showAccountPendingModal') === 'true') {
      shouldShowModal = true;
      sessionStorage.removeItem('showAccountPendingModal');
      modalHandled = true;
    }
    
    // Check for account pending modal flag in location state
    if (location.state?.showAccountPendingModal === true) {
      shouldShowModal = true;
      modalHandled = true;
    }
    
    // Set the modal state if needed
    if (shouldShowModal) {
      setShowAccountPendingModal(true);
    }
    
    // Handle message from location state
    if (location.state?.message) {
      const message = location.state.message;
      if (message.includes("declined") || message.includes("Declined")) {
        setPendingMessage(message);
      }
    }
    
    // Clean up navigation state if we handled a modal or message
    if (modalHandled || location.state?.message) {
      window.history.replaceState({}, document.title);
    }
    
    const alreadyAuthenticated =
      token && user?.role?.toLowerCase() === "fundraiser";
    if (
      alreadyAuthenticated &&
      !location.state?.skipAutoRedirect &&
      sessionStorage.getItem("skip_auto_redirect") !== "true"
    ) {
      const autoRedirect = async () => {
        try {
          const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
          const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
          const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

          if (!detailsSubmitted || !chargesEnabled) {
            if (detailsSubmitted && !chargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }
        } catch (error) {
          navigate("/fundraiser/welcome", { replace: true });
          return;
        }

        const userStatusRaw = user?.status;
        const userStatus =
          typeof userStatusRaw === "string"
            ? userStatusRaw.toLowerCase()
            : userStatusRaw;
        const isApproved = userStatus === "approved";

        if (isApproved) {
          navigate("/fundraiser/dashboard", { replace: true });
        } else {
          dispatch(clearAuth());
          sessionStorage.setItem("showAccountPendingModal", "true");
          navigate("/fundraiser/account-login", { replace: true });
        }
      };

      autoRedirect();
    }
  }, [location, token, user, navigate, dispatch]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      // Clear any previous error messages on new submission
      setAuthErrorMessage("");
      setPendingMessage("");
      setSuccessMessage("");
      
      try {
        const promiseResult = await dispatch(
          login({
            email: values.email,
            password: values.password,
            fcmToken: getFcmToken(),
          })
        ).unwrap();

        if (promiseResult?.success !== true) {
          setAuthErrorMessage("Invalid email or password");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "Invalid email or password");
          formik.setFieldError("password", "Invalid email or password");
          // Clear auth in case token was set
          dispatch(clearAuth());
          return;
        }

        const user = promiseResult?.data?.user || promiseResult?.user;
        const tempToken = promiseResult?.data?.token;
        const authHeaders = tempToken
          ? {
              Authorization: `Bearer ${tempToken}`,
              "api-key": process.env.REACT_APP_API_KEY,
            }
          : null;
        
        // Normalize role and status for robustness (e.g., "FUNDRAISER", "APPROVED")
        const userRoleRaw = user?.role;
        const userRole = typeof userRoleRaw === "string" ? userRoleRaw.toLowerCase() : userRoleRaw;
        
        // Block User/Member accounts
        if (userRole === "user" || userRole === "member") {
          setAuthErrorMessage("This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          formik.setFieldError("password", "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          dispatch(clearAuth());
          return;
        }
        
        if (userRole && userRole !== "fundraiser") {
          setAuthErrorMessage("Invalid email or password");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "Invalid email or password");
          formik.setFieldError("password", "Invalid email or password");
          // Clear auth in case token was set
          dispatch(clearAuth());
          return;
        }

        // Check Stripe onboarding status
        const fetchStripeStatus = async () => {
          if (!authHeaders) {
            throw new Error("Missing auth token");
          }
          const statusResponse = await axios.get("payout/onboardingStatus", {
            headers: authHeaders,
          });
          return statusResponse?.data?.data;
        };

        try {
          const stripeStatus = await fetchStripeStatus();
          const detailsSubmitted = stripeStatus?.detailsSubmitted === true;
          const chargesEnabled = stripeStatus?.chargesEnabled === true;

          if (!detailsSubmitted || !chargesEnabled) {
            dispatch(
              setLoginData({
                email: values.email,
                password: values.password,
                fcmToken: getFcmToken(),
              })
            );

            if (detailsSubmitted && !chargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }
        } catch (error) {
          dispatch(
            setLoginData({
              email: values.email,
              password: values.password,
              fcmToken: getFcmToken(),
            })
          );
          navigate("/fundraiser/welcome", { replace: true });
          return;
        }

        // Stripe is complete, now check approval status (only block if declined)
        let fundraiserProfile;
        try {
          if (!authHeaders) {
            throw new Error("Missing auth token");
          }
          const profileResponse = await axios.get("fundraiser/profile", {
            headers: authHeaders,
          });
          fundraiserProfile = profileResponse?.data?.data;
        } catch (profileError) {
          console.error("Failed to fetch fundraiser profile:", profileError);
          dispatch(
            setLoginData({
              email: values.email,
              password: values.password,
              fcmToken: getFcmToken(),
            })
          );
          sessionStorage.setItem("showAccountPendingModal", "true");
          navigate("/fundraiser/dashboard", {
            replace: true,
            state: { skipAutoRedirect: true },
          });
          return;
        }

        // Check admin approval status from the profile
        const profileStatus = typeof fundraiserProfile?.status === "string" 
          ? fundraiserProfile.status.toLowerCase() 
          : fundraiserProfile?.status;

        const pendingApprovalMessage = "Your account is pending admin approval. Please wait for approval.";

        if (profileStatus === "declined") {
          dispatch(clearAuth());
          setAuthErrorMessage("Your account has been declined. Please contact support.");
          setPendingMessage("Your account has been declined. Please contact support.");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "Your account has been declined. Please contact support.");
          formik.setFieldError("password", "Your account has been declined. Please contact support.");
          return;
        }

        if (profileStatus !== "approved") {
          dispatch(clearAuth());
          setAuthErrorMessage(pendingApprovalMessage);
          setPendingMessage(pendingApprovalMessage);
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", pendingApprovalMessage);
          formik.setFieldError("password", pendingApprovalMessage);
          sessionStorage.setItem("showAccountPendingModal", "true");
          return;
        }

        // Approved - Stripe complete - Set login data and go to dashboard
        dispatch(
          setLoginData({
            email: values.email,
            password: values.password,
            fcmToken: getFcmToken(),
          })
        );
        navigate("/fundraiser/dashboard", { replace: true });
      } catch (err) {
        setAuthErrorMessage("Invalid email or password");
        formik.setTouched({ email: true, password: true });
        formik.setFieldError("email", "Invalid email or password");
        formik.setFieldError("password", "Invalid email or password");
        // Clear auth in case token was set
        dispatch(clearAuth());
      }
    },
  });

  const { email, password } = formik.values;

  const { handleChange, handleBlur, handleSubmit, errors, touched } = formik;

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSignupClick = () => {
    navigate("/fundraiser/getting-started")
  };

  const handleCloseAccountPendingModal = () => {
    setShowAccountPendingModal(false);
  };

  return (
    <form
      className="w-full py-10 h-screen flex flex-col items-center offerform"
      onSubmit={handleSubmit}
    >
      {loading && <Loader />}
      <p className="font-poppins font-[500] text-[14px] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030] px-4">
        Welcome to Swoodle! Let's set up your Account!
      </p>
      
      <div className="flex flex-col items-center w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%]">
        <img src={SwoodleUpdatedLogo} alt="Swoodle" className="pt-[6%] pb-[2%] w-[60%]"></img>
        <p className="font-poppins font-[500] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030]">
          FUNDRAISER
        </p>
        <p className="font-poppins font-[400] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[18px] xl:text-[22px] text-[#303030] mt-6 sm:mt-10 mb-3 text-center">
          Your Account
        </p>
        {successMessage && (
          <div className="w-full bg-green-50 border border-green-200 rounded-md p-3 mb-3">
            <p className="text-[12px] sm:text-[14px] text-green-800 font-poppins text-center">
              {successMessage}
            </p>
          </div>
        )}
        {pendingMessage && (
          <div className="w-full bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
            <p className="text-[12px] sm:text-[14px] text-yellow-800 font-poppins text-center">
              {pendingMessage}
            </p>
          </div>
        )}
        {/* Removed top-level auth error banner; inline field errors remain */}
        <div className="flex flex-col justify-start items-start gap-2 w-full">
          <TextInput
            Icon={emailIcon}
            name={"email"}
            value={email}
            placeholder={"Email ID"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            inputDivClass={"h-[55px] sm:h-[65px]"}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
            autoComplete={"new-password"}
          />

          <TextInput
            Icon={passwordIcon}
            type={showPassword ? "text" : "password"}
            name={"password"}
            value={password}
            placeholder={"Password"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            inputDivClass={"h-[55px] sm:h-[65px]"}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.password || authErrorMessage}
            touched={touched.password}
            showPassword={showPassword}
            togglePassword={togglePassword}
            autoComplete={"new-password"}
          />
          <div className="w-full flex items-center justify-between sm:mt-0 2xl:mt-[0%]">
            <div className=" w-[60%] flex items-center customize">
              <Checkbox
                text="Remember me"
                divClassName="flex items-center justify-center"
                checked={rememberCheckbox}
                onChecked={() => {
                  setRememberCheckbox(!rememberCheckbox);
                }}
                textClass="lg:text-[14px] sm:text-[12px] font-[400] font-poppins text-[#767676] ml-4 whitespace-nowrap"
                UncheckedIcon={UncheckedIcon}
              />
            </div>

            <div className="w-[50%] text-right">
              <p className="text-[10px] sm:text-[12px] lg:text-[14px] font-[400] text-[#767676] font-poppins">
                <NavLink to="/forget-password">Forget Password</NavLink>
              </p>
            </div>
          </div>
        </div>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          <button
            type="button"
            className="text-[#00ADEF] font-[500] cursor-pointer hover:underline"
            onClick={() => {
              const mailto = "mailto:support@swoodle.com.au";
              try {
                const newWin = window.open(mailto, "_blank");
                if (!newWin) {
                  // Fallback: programmatically click a temporary anchor
                  const a = document.createElement("a");
                  a.href = mailto;
                  a.target = "_blank";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              } catch (_) {
                window.location.href = mailto;
              }
            }}
          >
            Need Help?
          </button>
        </p>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          Don't have a Fundraiser account?
          <span
            className="text-[#00ADEF] font-[500] cursor-pointer"
            onClick={handleSignupClick}
          >
            &nbsp; Sign up with Swoodle
          </span>
        </p>
        <div className="text-center w-full mt-4 sm:mt-5">
          <PrimaryButton
            type={"submit"}
            className={
              "w-full font-poppins text-[12px] sm:text-[14px] lg:text-[16px] text-center font-[500] h-[40px] sm:h-[45px] md:h-[50px] lg:h-[55px] rounded-[10px] mb-4 sm:mb-5 tracking-wide"
            }
            label="LOG INTO SWOODLE FUNDRAISER CENTRE"
          />
        </div>
      </div>

      {/* Account Pending Modal - Only for newly created fundraisers who completed Stripe */}
      {showAccountPendingModal && (
        <div
          className="offer-success-modal-backdrop"
          onClick={handleCloseAccountPendingModal}
        >
          <div
            className="offer-success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Account Pending</h3>
            <p className="offer-success-message">
              Your approval has been sent to the admin. Once the admin approves your account, you will receive an email notification.
            </p>
            <div className="offer-success-actions">
              <PrimaryButton
                className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                label="OK"
                onClick={handleCloseAccountPendingModal}
              />
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default AccountLogin;